<template>
  <section class="blob-browser">
    <ul class="breadcrumbs">
      <li :class="`crumb ${(index == breadCrumbs.length - 1) ? 'crumb-active' : ''}`" :key="index" v-for="(crumb, index) in breadCrumbs">
        <h6><span :title="crumb" @click="openBreadCrumb(index, crumb)">{{ crumb | truncate(25, true) }}</span></h6>
      </li>
    </ul>

    <b-table
      :data="documents"
      :loading="false"
      :default-sort-direction="defaultSortDirection"
      default-sort="name"
      hoverable
      striped>
      <b-table-column field="isDirectory" label="Type" v-slot="props" width="30" :custom-sort="sortByType" header-class="is-uppercase" sortable centered numeric>
        <b-icon pack="far" :icon="getTypeIcon(props.row)"></b-icon>
      </b-table-column>

      <b-table-column field="name" label="Title" v-slot="props" :custom-sort="sortByName" header-class="is-uppercase" sortable>
        <span class="pointer" :title="props.row.name" v-if="props.row.isDirectory" @click="openDirectory(props.row)">{{ props.row.name | truncate(75, true) }}</span>
        <span class="pointer" :title="props.row.name" v-if="!props.row.isDirectory" @click="download(props.row)">{{ normalizeFileName(props.row.name) | truncate(90, false) }}</span>
      </b-table-column>

      <b-table-column field="modifiedOn" label="Modified" v-slot="props" width="110" header-class="is-uppercase" sortable centered>
        {{ props.row.modifiedOn ? format(new Date(props.row.modifiedOn), 'yyyy-MM-dd') : '-' }}
      </b-table-column>
      
      <b-table-column field="length" label="Size" v-slot="props" width="85" header-class="is-uppercase" sortable centered>
        {{ props.row.isDirectory ? '-' : $numeral(props.row.length).format('0.0 b') }}
      </b-table-column>

      <b-table-column field="md5sum" label="Md5Sum" v-slot="props" width="150"  header-class="is-uppercase" v-if="!!isSoftwareContainer">
        {{ props.row.isDirectory ? '-' : props.row.md5sum }}
      </b-table-column>

      <b-table-column label="Actions" v-slot="props" header-class="is-uppercase">
        <div class="pointer blue" v-if="!props.row.isDirectory">
          <span @click="copyStorageLink(container, extractPathFromAbsolutePath(props.row.absolutePath))" alt="Copy Link">
            <b-tooltip label="Copy Link" type="is-light" animated>
              <b-icon pack="far" icon="copy"></b-icon>
            </b-tooltip>
          </span>
          <span @click="download(props.row)">
            <b-tooltip v-if="isPdf(props.row)" label="View File" type="is-light" animated>
              <b-icon pack="far" icon="file-export"></b-icon>
            </b-tooltip>
            <b-tooltip v-else label="Download File" type="is-light" animated>
              <b-icon pack="far" icon="download"></b-icon>
            </b-tooltip>
          </span>
        </div>
      </b-table-column>
      
      <template slot="empty">
        <section class="section">
          <div class="has-text-grey has-text-centered">
            <p>This folder is empty.</p>
          </div>
        </section>
      </template>
    </b-table>
  </section>
</template>
<script>
import { format } from 'date-fns';
import { copyToClipboardMixin } from '../mixins/copyToClipboardMixin';
import { normalizeFileNameMixin } from '../mixins/normalizeFileNameMixin';

// acquire index of end of match
// eslint-disable-next-line no-extend-native, func-names
String.prototype.indexOfEnd = function (string) { // NOTE: this needs to remain a plain `function` instead of using a lambda expression
  const io = this.indexOf(string);
  return io == -1 ? -1 : io + string.length;
}

export default {
  name: 'BlobBrowserComponent',
  props: {
    rootDir: String,
    container: String,
    blobs: {
      type: Array,
      default: () => []
    },
    topic: String
  },
  mixins: [
    copyToClipboardMixin,
    normalizeFileNameMixin
  ],
  filters: {
    truncate(value, length, isDirectory) {
      if (value.length > length) {
        if (isDirectory) {
          return `${value.substr(0, length)}...`;
        }
        
        // test with regex and see if the value ends with a known file extension
        const fileExtensionRegEx = new RegExp('(^.*.(doc|docx|pdf|txt|rtf|odt|xls|xlsx|csv|zip)$)');
        // when truncating files, include the file extension
        let truncated = '';
        if (value.includes('.') && fileExtensionRegEx.test(value)) {
          const segments = value.split('.');
          truncated = `${value.substr(0, length)}...${segments.pop()}`;
        } else {
          truncated = `${value.substr(0, length)}...`;
        }
        return truncated;        
      }
      return value;
    }
  },
  created() {    
    // check for hot-linked directory
    const { openDir } = this.$route.query;
    if (openDir) { 
      // navigate to the the specified directory
      this.path = openDir; 
      this.breadCrumbs = this.getBreadCrumbs(this.path);
    }
  },
  data() {
    return {
      path: this.rootDir,
      defaultSortDirection: 'desc',
      currentDir: this.rootDir,
      breadCrumbs: this.getBreadCrumbs(this.rootDir),
      format,
      isLoading: false
    }
  },
  methods: {
    extractPathFromAbsolutePath(absolutePath) {
      // define environment neutral url segment
      const segment = 'blob.core.windows.net';
      // acquire index of segment
      const uriSegIndex = absolutePath.indexOf(segment);
      // extract relative path
      return absolutePath.substring(segment.length + uriSegIndex);
    },
    async download(row) {
      const view = this.isPdf(row);
      const documentUrl = await this.$store.dispatch('downloadStorageFile', { container: this.container, absolutePath: row.absolutePath, view });
      if (!documentUrl) {
        this.$buefy.toast.open({
          duration: 2500,
          message: 'Error downloading file.',
          type: 'is-danger'
        });
        return;
      }
      if (view && documentUrl && documentUrl.includes('.pdf')) {
        const a = document.createElement('a');
        a.download = row.name;
        a.target = '_blank';
        a.href = `${documentUrl}`;
        document.body.appendChild(a);
        a.click();
        a.remove();
      } else {
        // eslint-disable-next-line no-restricted-globals
        location = documentUrl;
      }
    },
    async openDirectory(directory) {
      this.isLoading = true;
      try {
        let segments = directory.absolutePath.split('/');
        // reduce to remove domain
        segments = segments.splice(3, segments.length);
        // find index of current directory
        const idx = segments.indexOf(directory.name);
        // reduce to exclude elements beyond current directory
        segments = segments.splice(0, idx + 1);
        // join path together to acquire data for selected directory
        this.path = `/${segments.join('/')}`;
    
        // set browser history  
        this.$router.push({ 
          query: { 
            rootDir: this.rootDir, 
            openDir: this.path 
          }
        });
        // keep context of current directory
        this.currentDir = this.path;
        // update breadcrumbs
        this.breadCrumbs = this.getBreadCrumbs(this.currentDir);
      } catch (err) {
        this.$buefy.toast.open({
          duration: 2500,
          message: 'Error retrieving directory contents.',
          type: 'is-danger'
        });
      }
      this.isLoading = false;
    },
    // eslint-disable-next-line no-unused-vars
    async openBreadCrumb(crumbIndex, crumb) {
      this.isLoading = true;
      const newCrumbs = this.breadCrumbs.map(r => r);
      newCrumbs.splice(crumbIndex + 1, newCrumbs.length);
      newCrumbs.shift();
      newCrumbs.unshift(this.rootDir);
      
      this.path = newCrumbs.join('/');

      try {        
        // set browser history
        this.$router.push({ 
          query: { 
            rootDir: this.rootDir, 
            openDir: this.path 
          }
        });
        this.currentDir = crumbIndex === 0 ? this.rootDir : this.path;
        this.breadCrumbs = this.getBreadCrumbs(this.currentDir);
      } catch (err) {
        this.$buefy.toast.open({
          duration: 2500,
          message: 'Error retrieving directory contents.',
          type: 'is-danger'
        });
      }

      this.isLoading = false;
    },
    getDirectoryContents(blobs, path) {
      const dirContents = [];
      const pathInclude = `${path}/`;
      if (Array.isArray(blobs)) {
        if (blobs.length == 0) {
          blobs.push({ 
            isDirectory: false, 
            length: 0, 
            name: '', 
            absolutePath: 'placeholder',
            md5sum: ''
          });
        } else {         
          // iterate blobs
          blobs.forEach(blob => {
            const absoluteUri = blob.uri;

            // identify end of index for path
            const pathIndex = absoluteUri.indexOfEnd(path);
            if (pathIndex != -1 && absoluteUri.includes(pathInclude)) {
              // acquire index of next backslash, indicating another directory level
              const dirIndex = absoluteUri.indexOf('/', pathIndex + 1);
              if (dirIndex != -1) {
                // we know it is a directory because another backslash was found
                const directory = absoluteUri.substring(pathIndex + 1, dirIndex);

                // check for duplicates
                const exists = dirContents.find(item => item.name == directory);
                if (!exists) {
                  // add the directory
                  dirContents.push({
                    isDirectory: true, 
                    length: 0, 
                    name: directory, 
                    absolutePath: blob.uri,
                    contentType: null,
                    modifiedOn: null,
                    md5sum: null
                  });
                }
              } else {
                // not a directory, so must be a file
                dirContents.push({
                  isDirectory: false, 
                  length: blob.fileSize, 
                  name: blob.fileName, 
                  absolutePath: blob.uri,
                  contentType: blob.contentType,
                  modifiedOn: blob.modifiedOn,
                  md5sum: blob.fileHash
                });
              }
            } else {
              // no items found at path
              // this should not ever happen...
            }
          });
        }
      }

      // only remove the first element if it is the placeholder for an empty dataset 
      // (no documents retrieved)
      if (
        dirContents.length == 1 
        && dirContents[0].absolutePath == 'placeholder'
      ) {
        // remove the placeholder, this retains the column headers but empties the list
        setTimeout(() => {
          dirContents.pop();
        }, 10);
      }
      return dirContents;
    },

    // #region utility
    isPdf(row) {
      return row.name.includes('.pdf');
    },
    getBreadCrumbs(currentDir) {
      if (!currentDir) {
        return this.$buefy.toast.open({
          duration: 2500,
          message: 'Error attempting to set breadcrumbs.',
          type: 'is-danger'
        });
      }
      // if user is on root directory, just return main      
      if (this.rootDir == currentDir) return [this.topic];

      // decode URI to remove special character handling
      const decodedUri = decodeURI(currentDir);
      // grab index of root directory (should always be 0)
      const indexOfRoot = decodedUri.lastIndexOf(this.rootDir);
      // get the index at the END of the Root directory
      const lastIndexOfRootEnd = indexOfRoot == -1 ? -1 : indexOfRoot + this.rootDir.length;
      // set path to what comes after Root path
      const path = decodedUri.substring(lastIndexOfRootEnd);

      // segregate the path relative to the root directory
      const segments = path.split('/');
      segments.shift();
      segments.unshift(this.topic);
      return segments;
    },
    getFileNameExtension(item) {
      if (!item) return 'unknown';
      return `.${item.name.split('.').pop()}`;
    },
    getNameNoExtension(item) {
      if (!item) return 'Unknown';
      const segments = item.name.split('.');
      segments.pop();
      return segments.join('.');
    },
    // #endregion

    // #region table sorting & formatting
    sortByName(a, b, isAsc) {
      // prioritize directories above files
      if (a.isDirectory && !b.isDirectory) {
        return isAsc ? 1 : -1;
      }

      // when comparing directories, use their names
      if (a.isDirectory && b.isDirectory) {
        // eslint-disable-next-line no-nested-ternary
        return isAsc
          ? a.name < b.name ? 1 : -1
          : a.name > b.name ? 1 : -1;
      }

      // acquire the parsed names of the directory items
      const parsedA = this.normalizeFileName(a.name).toLowerCase();
      const parsedB = this.normalizeFileName(b.name).toLowerCase();

      // toggle sort direction based on isAsc (is ascending)
      // eslint-disable-next-line no-nested-ternary
      return isAsc
        ? parsedA < parsedB ? 1 : -1
        : parsedA > parsedB ? 1 : -1;
    },
    sortByType(a, b, isAsc) {
      // prioritize directories above files
      if (a.isDirectory && !b.isDirectory) {
        return isAsc ? 1 : -1;
      }

      // when comparing directories, use their names
      if (a.isDirectory && b.isDirectory) {
        // eslint-disable-next-line no-nested-ternary
        return isAsc
          ? a.name < b.name ? 1 : -1
          : a.name > b.name ? 1 : -1;
      }

      // acquire the file extensions to compare
      const aExtension = a.name.split('.').pop();
      const bExtension = b.name.split('.').pop();
      
      // compare file extensions to sort by type of file
      // eslint-disable-next-line no-nested-ternary
      return isAsc
        ? aExtension < bExtension ? 1 : -1
        : aExtension > bExtension ? 1 : -1;
    },
    // Extract file type extension and present an appropriate icon.
    getTypeIcon(row) {
      if (row.isDirectory) return 'folder';
      const extension = row.name.split('.').pop();
      switch (extension) {
        case 'pdf':
          return 'file-pdf';
        case 'xls':
        case 'xlsx':
          return 'file-excel';
        case 'docx':
        case 'doc':
          return 'file-word';
        case 'zip':
          return 'file-archive';
        default:
          return 'file';
      }
    }
    // #endregion
  },
  computed: {
    documents() {
      return this.getDirectoryContents(this.blobs, this.path);
    },
    isSoftwareContainer() {
      return this.container == 'software-downloads';
    }
  }
}
</script>
<style lang="scss" scoped>
@import './../../styles/_colors';

::v-deep .table-wrapper {
  overflow: hidden;
}
.actions {
  .icon {
    padding-right: 20px;
    padding-left: 10px;
  }
}
.breadcrumbs {
  padding-left: 0;
  padding-top: 7px;
  margin-top: 0;
  margin-bottom: 5px;
  list-style: none;
  display: flex;
  flex-wrap: wrap;

  .crumb{
    color: $blue;
    display: inline-block;
    position: relative;       
    padding-right: 15px;
    padding-left: 15px;
    margin-top: 5px;
    cursor: pointer;
  }
  
  .crumb:after{
    color: $black;
    content: "/";
    line-height: 1;
    text-align: center;
    position: absolute;
    top: 40%;
    right: 0;
    transform: translateY(-50%);
  }
  
  .crumb-active{
    margin-right: 0;
    padding-right: 0;
    color: $grey;
  }
  
  .crumb-active:after{
    display: none;
  }
}
.download-action {
  cursor: pointer;
  color: $blue;
}
.loading {
  margin-top: 32px;
}
</style>
